<template>
    <Toast/>
    <div class="grid">
        <div class="col-12">
            <div class="card">
                <div class="flex flex-column md:flex-row md:justify-content-between md:align-items-center mb-4">
                    <h5 class="m-0">Cuentas Financieras</h5>
                    <Button v-if="isAdmin" label="Nueva Cuenta" icon="pi pi-plus" class="p-button-success mt-2 md:mt-0" @click="openNew" />
                </div>
                <DataView :value="accounts" layout="grid">
                    <template #grid="slotProps">
                        <div class="col-12 md:col-4 p-3">
                            <div class="fa-card" @click="viewAccountDetail(slotProps.data)">
                                <div class="fa-left">
                                    <div class="fa-name">
                                        {{ slotProps.data.name }}
                                    </div>
                                    <div class="fa-accno">{{ getAccountNumberDisplay(slotProps.data) }}</div>
                                </div>
                                <div class="fa-center">
                                    <div class="fa-label">Saldo disponible</div>
                                    <div class="fa-balance">
                                        $ {{ formatBalance(slotProps.data.balance) }}
                                    </div>
                                </div>
                                <div class="fa-right">
                                    <div class="fa-type">
                                        <template v-if="slotProps.data.moneyType === 'TRANSFER' && slotProps.data.bank">
                                            <img v-if="slotProps.data.bank.icon" 
                                                 :src="$baseURL + slotProps.data.bank.icon" 
                                                 :alt="slotProps.data.bank.name"
                                                 class="bank-icon"
                                            />
                                            <span class="bank-name">{{ slotProps.data.bank.name }}</span>
                                        </template>
                                        <span v-else>{{ getAccountLabel(slotProps.data) }}</span>
                                    </div>
                                    <div class="fa-actions">
                                        <Button v-if="isAdmin" icon="pi pi-pencil" class="p-button-rounded p-button-text p-button-sm" @click.stop="editAccount(slotProps.data)" />
                                        <Button v-if="isAdmin" icon="pi pi-trash" class="p-button-rounded p-button-text p-button-danger p-button-sm" @click.stop="confirmDeleteAccount(slotProps.data)" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                </DataView>

                <Dialog v-model:visible="accountDialog" :style="{width: '450px', maxWidth: '90vw'}" header="Nueva Cuenta Financiera" :modal="true" class="p-fluid">
                    <div class="field">
                        <label for="moneyType">Tipo de cuenta</label>
                        <Dropdown 
                            id="moneyType" 
                            v-model="account.moneyType" 
                            :options="moneyTypes" 
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Seleccione una opción"
                            :disabled="account.id !== null"
                            :class="{'p-invalid': submitted && !account.moneyType}"
                        />
                        <small class="p-invalid" v-if="submitted && !account.moneyType">Debe seleccionar el tipo de dinero.</small>
                    </div>

                    <div v-if="account.moneyType === 'CARD'" class="field">
                        <label for="acquirer">Pasarela de pago</label>
                        <Dropdown
                            id="acquirer"
                            v-model="account.acquirer"
                            :options="gateways"
                            optionLabel="name"
                            optionValue="code"
                            placeholder="Seleccione su pasarela"
                            showClear
                        />
                        <small class="fa-hint">
                            Solo aparecen las pasarelas cuyas liquidaciones sabemos conciliar.
                            Si la tuya no está, déjala en blanco: la cuenta registra tus cobros igual.
                        </small>
                    </div>

                    <div v-if="account.moneyType === 'TRANSFER'">
                        <div class="field">
                            <label for="bank">Banco</label>
                            <Dropdown 
                                id="bank" 
                                v-model="account.bank" 
                                :options="banks" 
                                optionLabel="name"
                                placeholder="Seleccione un banco"
                                :class="{'p-invalid': submitted && !account.bank}"
                            >
                                <template #value="slotProps">
                                    <div v-if="slotProps.value" class="flex align-items-center gap-2">
                                        <img v-if="slotProps.value.icon" 
                                             :src="$baseURL + slotProps.value.icon" 
                                             :alt="slotProps.value.name"
                                             class="dropdown-bank-icon"
                                        />
                                        <span>{{ slotProps.value.name }}</span>
                                    </div>
                                    <span v-else>{{ slotProps.placeholder }}</span>
                                </template>
                                <template #option="slotProps">
                                    <div class="flex align-items-center gap-2">
                                        <img v-if="slotProps.option.icon" 
                                             :src="$baseURL + slotProps.option.icon" 
                                             :alt="slotProps.option.name"
                                             class="dropdown-bank-icon"
                                        />
                                        <span>{{ slotProps.option.name }}</span>
                                    </div>
                                </template>
                            </Dropdown>
                            <small class="p-invalid" v-if="submitted && !account.bank">El banco es requerido.</small>
                        </div>

                        <div class="field">
                            <label for="accountType">Tipo de Cuenta Bancaria</label>
                            <Dropdown 
                                id="accountType" 
                                v-model="account.accountType" 
                                :options="accountTypes" 
                                optionLabel="name"
                                dataKey="code"
                                placeholder="Seleccione el tipo de cuenta"
                                :class="{'p-invalid': submitted && !account.accountType}"
                            />
                            <small class="p-invalid" v-if="submitted && !account.accountType">El tipo de cuenta es requerido.</small>
                        </div>

                        <div class="field">
                            <label for="accountNumber">Número de Cuenta</label>
                            <InputText 
                                id="accountNumber" 
                                v-model.trim="account.accountNumber" 
                                placeholder="Ej: 2100123456"
                                :class="{'p-invalid': submitted && !account.accountNumber}" 
                            />
                            <small class="p-invalid" v-if="submitted && !account.accountNumber">El número de cuenta es requerido.</small>
                        </div>
                    </div>

                    <div v-if="account.moneyType" class="field">
                        <label for="name">Nombre de la Cuenta</label>
                        <InputText 
                            id="name" 
                            v-model.trim="account.name" 
                            required="true"
                            :placeholder="getNamePlaceholder()"
                            :class="{'p-invalid': submitted && !account.name}" 
                        />
                        <small class="p-invalid" v-if="submitted && !account.name">El nombre es requerido.</small>
                    </div>

                    <template #footer>
                        <Button label="Cancelar" icon="pi pi-times" class="p-button p-button-secondary" @click="hideDialog"/>
                        <Button label="Guardar" icon="pi pi-check" class="p-button p-button-success" @click="saveAccount" />
                    </template>
                </Dialog>

                <Dialog v-model:visible="deleteAccountDialog" :style="{width: '450px', maxWidth: '90vw'}" header="Confirmar" :modal="true">
                    <div class="flex align-items-center justify-content-center">
                        <i class="pi pi-exclamation-triangle mr-3" style="font-size: 2rem" />
                        <span v-if="account">¿Está seguro de eliminar la cuenta <b>{{ account.name }}</b>?</span>
                    </div>
                    <template #footer>
                        <Button label="No" icon="pi pi-times" class="p-button p-button-secondary" @click="deleteAccountDialog = false"/>
                        <Button label="Sí" icon="pi pi-check" class="p-button p-button-danger" @click="deleteAccount" />
                    </template>
                </Dialog>
            </div>
        </div>
    </div>  
</template>

<script>
import Big from 'big.js';
import FinantialAccount from '../../models/finantialAccount';
import httpRequest from '../../service/httpRequest';
import { deleteOutcome } from '../../service/deleteOutcome';

export default {
    data() {
        return {
            accountDialog: false,
            deleteAccountDialog: false,
            account: {},
            submitted: false,
            moneyTypes: [
                { label: '💵 Efectivo', value: 'CASH' },
                { label: '🏦 Transferencia (Cuenta bancaria)', value: 'TRANSFER' },
                { label: '💳 Tarjeta (Pasarela de pago)', value: 'CARD' }
            ],
            banks: [
                { name: 'Banco Pichincha', icon: 'https://via.placeholder.com/50' },
                { name: 'Banco del Pacífico', icon: 'https://via.placeholder.com/50' },
                { name: 'Banco Guayaquil', icon: 'https://via.placeholder.com/50' },
                { name: 'Produbanco', icon: 'https://via.placeholder.com/50' },
                { name: 'Banco Internacional', icon: 'https://via.placeholder.com/50' },
                { name: 'Banco Bolivariano', icon: 'https://via.placeholder.com/50' }
            ],
            accountTypes: [
                { name: 'Ahorros', code: 'SAVING' },
                { name: 'Corriente', code: 'CHECKING' }
            ],
            // Pasarelas aceptadas. Sin dummies a propósito: ofrecer una que el backend
            // no acepta termina en "Pasarela de pago desconocida" al guardar.
            gateways: [],
            accounts: [],
        };
    },
    created() {
        this.loadBanks();
        this.loadGateways();
        this.loadAccounts();
    },
    computed: {
        isAdmin() {
            // Espejo del decorador backend: crear/editar/eliminar cuentas es admin-only.
            return this.$can('finantialAccountsManage');
        }
    },
    methods: {
        async loadBanks() {
            try {
                const response = await httpRequest.getBanks();
                if (response.status === 200 && response.data) {
                    this.banks = response.data.map(item => (
                      { name: item.name, icon: item.icon }
                    ));
                } else {
                    this.$toast.add({
                        severity: 'warn',
                        summary: 'Advertencia',
                        detail: 'No se pudieron cargar los bancos',
                        life: 3000
                    });
                }
            } catch (error) {
                console.error('Error al cargar bancos:', error);
                // Mantener los bancos dummy si falla la carga
            }
        },
        async loadGateways() {
            try {
                const response = await httpRequest.getPaymentGateways();
                if (response.status === 200 && response.data) {
                    this.gateways = response.data;
                }
            } catch (error) {
                console.error('Error al cargar pasarelas:', error);
                // Se queda vacía: el campo es opcional y no bloquea crear la cuenta.
            }
        },
        async loadAccounts() {
            try {
                const response = await httpRequest.getFinantialAccounts();
                if (response.status === 200 && response.data) {
                    this.accounts = response.data.map(item => new FinantialAccount(item));
                } else {
                    console.error('Error al cargar cuentas:', response);
                    this.$toast.add({
                        severity: 'warn',
                        summary: 'Advertencia',
                        detail: 'No se pudieron cargar las cuentas financieras',
                        life: 3000
                    });
                }
            } catch (error) {
                console.error('Error al cargar cuentas:', error);
                // Mantener las cuentas dummy si falla la carga
            }
        },
        openNew() {
            this.account = {
                id: null,
                name: '',
                moneyType: '',
                bank: null,
                acquirer: null,
                accountType: '',
                accountNumber: ''
            };
            this.submitted = false;
            this.accountDialog = true;
        },
        hideDialog() {
            this.accountDialog = false;
            this.submitted = false;
        },
        saveAccount() {
            this.submitted = true;

            // Validar campos básicos
            if (!this.account.name || !this.account.moneyType) {
                return;
            }

            // Validar campos adicionales si es TRANSFER
            if (this.account.moneyType === 'TRANSFER') {
                if (!this.account.bank || !this.account.accountType || !this.account.accountNumber) {
                    return;
                }
            }

            // Verificar duplicados
            if (this.isDuplicateAccount()) {
                if (this.account.moneyType === 'TRANSFER') {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Ya existe una cuenta con este banco y número de cuenta',
                        life: 4000
                    });
                } else if (this.account.moneyType === 'CARD') {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Ya existe una cuenta de tarjeta con esta pasarela y nombre',
                        life: 4000
                    });
                } else {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Ya existe una cuenta de efectivo con este nombre',
                        life: 4000
                    });
                }
                return;
            }

            // Preparar el objeto para enviar al backend
            const accountToSave = {
                id: this.account.id,
                name: this.account.name,
                moneyType: this.account.moneyType,
                bank: (this.account.bank) ? this.account.bank.name : null,
                acquirer: this.account.acquirer || null,
                accountType: this.account.accountType ? this.account.accountType.code.toUpperCase() : null,
                accountNumber: this.account.accountNumber
            };

            // Guardar en el backend
            this.saveAccountToBackend(accountToSave);
        },
        async saveAccountToBackend(accountData) {
            try {
                const response = await httpRequest.saveFinantialAccount(accountData);
                
                if (response.status === 200 && response.data) {
                    const savedAccount = new FinantialAccount(response.data);

                    if (accountData.id) {
                        // Actualizar cuenta existente
                        const index = this.accounts.findIndex(a => a.id === accountData.id);
                        if (index !== -1) {
                            this.accounts[index] = savedAccount;
                        }
                        this.$toast.add({
                            severity: 'success',
                            summary: 'Exitoso',
                            detail: 'Cuenta actualizada correctamente',
                            life: 3000
                        });
                    } else {
                        // Agregar nueva cuenta
                        this.accounts.push(savedAccount);
                        this.$toast.add({
                            severity: 'success',
                            summary: 'Exitoso',
                            detail: 'Cuenta creada correctamente',
                            life: 3000
                        });
                    }
                    
                    // Store finantial accounts
                    await this.$store.dispatch('setFinantialAccounts', this.accounts);

                    this.accountDialog = false;
                    this.account = {};
                } else {
                    console.error('Error al guardar cuenta:', response);
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'No se pudo guardar la cuenta. Intente nuevamente.',
                        life: 4000
                    });
                }
            } catch (error) {
                console.error('Error al guardar cuenta:', error);
                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Ocurrió un error al guardar la cuenta',
                    life: 4000
                });
            }
        },
        editAccount(account) {
            // Buscar el objeto completo de accountType si existe
            let accountTypeObj = null;
            if (account.accountType) {
                if (typeof account.accountType === 'string') {
                    // Si es un string (code), buscar el objeto completo en accountTypes
                    accountTypeObj = this.accountTypes.find(type => type.code === account.accountType);
                } else if (typeof account.accountType === 'object') {
                    // Si ya es un objeto, verificar que tenga un code válido
                    accountTypeObj = this.accountTypes.find(type => type.code === account.accountType.code);
                }
            }
            
            this.account = {
                id: account.id,
                name: account.name,
                moneyType: account.moneyType,
                bank: account.bank,
                // El backend serializa {code, name}; el dropdown trabaja con el code.
                acquirer: account.acquirer ? account.acquirer.code : null,
                accountType: accountTypeObj,
                accountNumber: account.accountNumber
            };

            this.accountDialog = true;
        },
        confirmDeleteAccount(account) {
            // Ninguna cuenta es especial por su nombre: el backend no deja borrar
            // una cuenta con movimientos y explica por qué (H56).
            this.account = account;
            this.deleteAccountDialog = true;
        },
        async deleteAccount() {
            try {
                const response = await httpRequest.deleteFinantialAccount(this.account.id);
                const outcome = deleteOutcome(response, [this.account.id], {
                    success: 'Cuenta eliminada correctamente',
                    failure: 'No se pudo eliminar la cuenta. Intente nuevamente.',
                });
                // Se saca de la lista solo lo que el backend confirmó borrado.
                this.accounts = this.accounts.filter(a => !outcome.deletedIds.includes(a.id));
                this.$toast.add(outcome.toast);
            } catch (error) {
                console.error('Error al eliminar cuenta:', error);
                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Ocurrió un error al eliminar la cuenta',
                    life: 4000
                });
            }
            
            this.deleteAccountDialog = false;
            this.account = {};
        },
        acquirerCode(account) {
            // El backend serializa la pasarela como {code, name}; el dropdown usa el code.
            return account && account.acquirer ? account.acquirer.code : null;
        },
        getMoneyTypeLabel(moneyType) {
            if (moneyType === 'CASH') return '💵 Efectivo';
            if (moneyType === 'TRANSFER') return '🏦 Cuenta Bancaria';
            if (moneyType === 'CARD') return '💳 Pasarela de pago';
            return 'No especificado';
        },
        getAccountLabel(account) {
            if (account.moneyType === 'CASH') {
                return '💵 Efectivo';
            }
            if (account.moneyType === 'TRANSFER' && account.bank) {
                return `🏦 ${account.bank.name}`;
            }
            if (account.moneyType === 'CARD') {
                return account.acquirer ? `💳 ${account.acquirer.name}` : '💳 Pasarela de pago';
            }
            return '🏦 Cuenta Bancaria';
        },
        getNamePlaceholder() {
            if (this.account.moneyType === 'TRANSFER') {
                return 'Ej: Cuenta ahorros 1, Mi cuenta principal, etc.';
            }
            if (this.account.moneyType === 'CASH') {
                return 'Ej: Caja chica, Efectivo principal, etc.';
            }
            if (this.account.moneyType === 'CARD') {
                return 'Ej: Datafast principal, Medianet POS local, etc.';
            }
            return '';
        },
        getAccountNumberDisplay(account) {
            if (account.moneyType === 'CASH' || account.moneyType === 'CARD') {
                return ''; // Sin número visible para CASH ni CARD
            }
            // Para TRANSFER, mostrar los últimos dígitos del accountNumber o id
            if (account.accountNumber) {
                const s = String(account.accountNumber);
                return '******' + s.slice(-4);
            }
            // Fallback al id si no hay accountNumber
            if (account.id) {
                const s = String(account.id);
                return '******' + s.slice(-3).padStart(3, '*');
            }
            return '---';
        },
        lastDigits(id) {
            if (id === null || id === undefined) return '---';
            const s = String(id);
            return s.slice(-3).padStart(3, '*');
        },
        formatBalance(balance) {
            try {
                if (balance == null) return '0.00';
                const b = balance instanceof Big ? balance : new Big(balance);
                // formato con 2 decimales y separador de miles simple
                return b.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            } catch (e) {
                return '0.00';
            }
        },
        isDuplicateAccount() {
            return this.accounts.some(existingAccount => {
                // No comparar con la misma cuenta si estamos editando
                if (this.account.id && existingAccount.id === this.account.id) {
                    return false;
                }

                // Para cuentas TRANSFER: verificar banco y número de cuenta
                if (this.account.moneyType === 'TRANSFER' && existingAccount.moneyType === 'TRANSFER') {
                    const sameBank = this.account.bank && existingAccount.bank && 
                                   this.account.bank.name === existingAccount.bank.name;
                    const sameAccountNumber = this.account.accountNumber === existingAccount.accountNumber;
                    return sameBank && sameAccountNumber;
                }

                // Para cuentas CARD: verificar misma pasarela y mismo nombre. Dos
                // cuentas SIN pasarela declarada también chocan si comparten nombre —
                // antes no, porque el vacío hacía falsa la comparación.
                if (this.account.moneyType === 'CARD' && existingAccount.moneyType === 'CARD') {
                    const sameGateway = (this.account.acquirer || null) === this.acquirerCode(existingAccount);
                    const sameName = this.account.name && existingAccount.name &&
                                   this.account.name.trim().toLowerCase() === existingAccount.name.trim().toLowerCase();
                    return sameGateway && sameName;
                }

                // Para cuentas CASH: verificar que no haya otra cuenta CASH con el mismo nombre
                if (this.account.moneyType === 'CASH' && existingAccount.moneyType === 'CASH') {
                    return this.account.name.trim().toLowerCase() === existingAccount.name.trim().toLowerCase();
                }

                return false;
            });
        },
        viewAccountDetail(account) {
            this.$router.push({
                path: '/dashboard/cuenta/detalle',
                query: { id: account.id }
            });
        }
    }
};
</script>

<style scoped>
.fa-list {
    width: 100%;
}

.fa-card {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1.5rem;
    background: var(--surface-card);
    border: 1px solid var(--surface-border);
    border-radius: var(--border-radius);
    transition: all 0.2s;
    height: 100%;
    cursor: pointer;
}

.fa-card:hover {
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
}

.fa-left {
    padding-bottom: 1rem;
    border-bottom: 1px solid var(--surface-border);
}

.fa-left .fa-name {
    font-weight: 700;
    font-size: 1.1rem;
    color: var(--text-color);
    margin-bottom: 0.5rem;
}

.fa-accno { 
    color: var(--text-color-secondary); 
    font-size: 0.9rem;
    font-family: monospace;
}

.fa-center { 
    text-align: left;
    flex: 1;
    padding: 1rem 0;
}

.fa-label { 
    color: var(--text-color-secondary); 
    font-size: 0.875rem;
    margin-bottom: 0.5rem;
}

.fa-balance { 
    font-size: 1.75rem; 
    font-weight: 700; 
    color: var(--text-color);
}

.fa-right { 
    display: flex; 
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding-top: 1rem;
    border-top: 1px solid var(--surface-border);
}

.fa-type { 
    color: var(--text-color-secondary); 
    font-size: 0.875rem;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1;
    min-width: 0;
}

.bank-icon {
    height: 24px;
    width: auto;
    object-fit: contain;
    flex-shrink: 0;
}

.bank-name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
}

.dropdown-bank-icon {
    height: 20px;
    width: auto;
    object-fit: contain;
}

.fa-hint {
    display: block;
    margin-top: 0.35rem;
    color: var(--text-color-secondary);
    line-height: 1.35;
}

.fa-actions {
    display: flex;
    gap: 0.25rem;
    flex-shrink: 0;
}
</style>
